/**
 * Sparkline - Mini bar chart for visualizing trends
 *
 * Shows a series of values as small bars, useful for displaying
 * recent match card totals or other trend data in compact spaces.
 */
import React from 'react'

interface SparklineProps {
  /** Array of values to display (oldest first for left-to-right reading) */
  values: number[]
  /** Height of the sparkline in pixels */
  height?: number
  /** Width of the sparkline in pixels */
  width?: number
  /** Bar color (CSS color value) */
  color?: string
  /** Show trend highlighting (green for rising, red for falling) */
  showTrend?: boolean
  /** Accessible label for screen readers */
  ariaLabel?: string
}

export function Sparkline({
  values,
  height = 20,
  width = 50,
  color = 'var(--accent)',
  showTrend = false,
  ariaLabel,
}: SparklineProps) {
  if (!values.length) return null

  const max = Math.max(...values, 1)

  // Calculate trend direction for color
  let trendColor = color
  if (showTrend && values.length >= 2) {
    const recent = values.slice(-2)
    if (recent[1] > recent[0]) {
      trendColor = 'var(--green)'
    } else if (recent[1] < recent[0]) {
      trendColor = 'var(--red)'
    }
  }

  // Generate description for screen readers
  const description = ariaLabel || `Sparkline showing ${values.length} values: ${values.join(', ')}`

  return (
    <div
      className="sparkline"
      style={{ height, width }}
      role="img"
      aria-label={description}
    >
      {values.map((value, index) => {
        const barHeight = max > 0 ? (value / max) * 100 : 0
        const isLast = index === values.length - 1
        return (
          <div
            key={index}
            className="sparkline__bar"
            style={{
              height: `${Math.max(barHeight, 5)}%`,
              background: showTrend && isLast ? trendColor : color,
              opacity: showTrend && !isLast ? 0.6 : 1,
            }}
          />
        )
      })}
    </div>
  )
}
